import { useMemo, useContext } from 'react';
import { IResources, isMediaMessage, isVoiceMessage } from '@enuan/chat__utils';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';

import { Image, Audio, Details } from '../../message/content';
import { CancelPreview } from './CancelPreview';
import { submitContext } from '../../../contexts/submit.context';
import { formMessages } from '../i18n';

/**
 * Attachment/voice preview shown in the submit form area.
 * Renders the appropriate media viewer (image, audio, document)
 * with a cancel button to discard the pending message.
 */
export const Preview = styled(
	({ className }: { className?: string }) => {
		const intl = useIntl();
		const variant = usePreviewVariant();

		return (
			<div
				className={classNames(
					'form__preview',
					variant && 'form__preview--media',
					variant?.className,
					className,
				)}
			>
				{variant ? (
					<MediaContent variant={variant} />
				) : (
					intl.formatMessage(formMessages.invalid_message)
				)}
			</div>
		);
	},
	{ label: 'Preview', target: 'Preview-root' },
)({
	width: '100%',
	overflowX: 'hidden',
	'& img': {
		border: '1px solid var(--chat__footer__form__preview_border)',
		borderRadius: '15px',
	},
	'& .form__cancel': {
		flexShrink: 0,
		justifySelf: 'flex-end',
		height: '1em',
		width: '1em',
		'& .icon.cancel': {
			verticalAlign: 'middle',
			fill: 'var(--chat__footer__form__action_color)',
			stroke: 'none',
		},
	},
	'&.form__preview--image': {
		display: 'flex',
		flexDirection: 'column',
		'& .message__image': {
			overflow: 'hidden',
			flex: 1,
		},
	},
});

// ── accessories (in order of appearance) ────────────────────────────

/** Discriminated union describing the kind of media being previewed. */
type PreviewVariant = {
	kind: 'image' | 'audio' | 'voice' | 'document';
	data: IResources;
	className: string;
};

/**
 * Determines the preview variant from the current message.
 * Returns `null` when the message cannot be previewed.
 */
function usePreviewVariant(): PreviewVariant | null {
	const { message } = useContext(submitContext);

	return useMemo(() => {
		if (isVoiceMessage(message)) {
			return {
				kind: 'voice',
				data: message._data,
				className: 'form__preview--voice',
			};
		}
		if (isMediaMessage(message)) {
			const mime = message._data.mime_type ?? '';
			if (mime.includes('image/')) {
				return {
					kind: 'image',
					data: message._data,
					className: 'form__preview--image',
				};
			}
			if (mime.includes('audio/')) {
				return {
					kind: 'audio',
					data: message._data,
					className: 'form__preview--audio',
				};
			}
			return {
				kind: 'document',
				data: message._data,
				className: 'form__preview--document',
			};
		}
		return null;
	}, [message]);
}

type MediaContentProps = {
	variant: PreviewVariant;
};

/**
 * Renders the media content matching the current preview variant.
 */
const MediaContent = ({ variant }: MediaContentProps) => {
	switch (variant.kind) {
		case 'image':
			return (
				<Image resource={variant.data}>
					<CancelPreview />
				</Image>
			);
		case 'audio':
		case 'voice':
			return (
				<Audio resource={variant.data} hideName={variant.kind === 'voice'} />
			);
		case 'document':
			return (
				<Details resource={variant.data}>
					<CancelPreview />
				</Details>
			);
	}
};
